/**
 * TriageExistingLeadPanel — 2026-10-22 (Deepika, triage coordinator).
 *
 * "Open existing lead" in the New lead box used to only filter the list to the number, so a coordinator
 * saw a bare row and thought the lead "did not open" (the coordinator role has no full lead page).
 * Now the existing lead OPENS here, above the table, with the actions a coordinator can take on it:
 * Assign / Reassign, Verify, and the bot chat (Responses). Nothing new on the server: it reads the row the
 * filtered list already loaded and reuses the row's own actions.
 */
import { useEffect, useState } from 'react';
import { formatRejectedOn, rejectReasonLabel, type DashboardRow, type Tab } from './triageDeskTypes';

export interface ExistingFocus {
  id: string;
  name: string | null;
  sourceName: string | null;
  assignedToName: string | null;
  location: string | null;
}

interface PanelCtx {
  existingFocus: ExistingFocus | null;
  setExistingFocus: (f: ExistingFocus | null) => void;
  rows: DashboardRow[];
  loading: boolean;
  openPicker: (row: DashboardRow, mode: 'assign' | 'reassign') => void;
  openVerify: (row: DashboardRow) => void;
  setResponsesOpenFor: (row: DashboardRow | null) => void;
  tab?: Tab;
  setTab?: (tab: Tab) => void;
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 truncate" title={value ?? ''}>{value || '—'}</div>
    </div>
  );
}

export function TriageExistingLeadPanel({ ctx }: { ctx: PanelCtx }) {
  const f = ctx.existingFocus;
  const row = f ? ctx.rows.find((r) => r.id === f.id) ?? null : null;
  // SUN-233 — a lead Triage marked Irrelevant is hidden from every list but the
  // Rejected tab. When the focused lead is not in this list, ask the Rejected
  // view for it so the banner can say so (and where to restore it).
  const [rejected, setRejected] = useState<{ leadId: string; rejectedAt: string | null; reason: string | null } | null>(null);
  const focusId = f?.id ?? null;
  const lookupNeeded = Boolean(focusId) && !row && !ctx.loading && ctx.tab !== 'rejected';
  useEffect(() => {
    if (!lookupNeeded || !focusId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/v1/leads/triage-dashboard?tab=rejected&leadId=${encodeURIComponent(focusId)}&limit=1`, { credentials: 'include' });
        if (!res.ok) return;
        const j = await res.json();
        const hit = Array.isArray(j?.data) ? j.data[0] : null;
        if (!cancelled && hit && hit.id === focusId) {
          setRejected({ leadId: focusId, rejectedAt: hit.rejectedAt ?? null, reason: hit.rejectReason ?? null });
        }
      } catch { /* banner falls back to the generic text */ }
    })();
    return () => { cancelled = true; };
  }, [lookupNeeded, focusId]);
  if (!f) return null;
  // On the Rejected tab the table row itself carries Restore.
  if (ctx.tab === 'rejected') return null;
  const rejectedHere = rejected && rejected.leadId === f.id ? rejected : null;
  const close = () => ctx.setExistingFocus(null);
  const owned = Boolean(row?.assignedToName || row?.teamName);

  return (
    <section role="region" aria-label="Existing lead" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Existing lead</div>
          <div className="text-lg font-semibold text-gray-900">{row?.name ?? f.name ?? 'Unnamed lead'}</div>
          <div className="text-sm text-gray-600">
            {[row?.phone, row?.source ?? f.sourceName, row?.location ?? f.location].filter(Boolean).join(' · ')}
          </div>
        </div>
        <button onClick={close} aria-label="Close existing lead" className="ml-auto rounded px-2 py-1 text-gray-500 hover:bg-amber-100">✕</button>
      </div>

      {!row && ctx.loading && <p className="mt-3 text-sm text-gray-600">Finding the lead…</p>}

      {!row && !ctx.loading && rejectedHere && (
        <p className="mt-3 text-sm text-gray-700" data-testid="rejected-banner">
          {rejectedHere.reason && rejectedHere.reason !== 'irrelevant'
            ? `This lead was rejected (${rejectReasonLabel(rejectedHere.reason)})`
            : 'This lead was marked Irrelevant'}
          {rejectedHere.rejectedAt ? ` on ${formatRejectedOn(rejectedHere.rejectedAt)}` : ''}. Open the{' '}
          <button
            type="button"
            onClick={() => ctx.setTab?.('rejected')}
            className="font-medium text-blue-700 underline hover:text-blue-900"
          >Rejected tab</button>{' '}to restore it.
        </p>
      )}

      {!row && !ctx.loading && !rejectedHere && (
        <p className="mt-3 text-sm text-gray-700">
          This lead is not in this list
          {f.assignedToName ? ` — it is assigned to ${f.assignedToName}` : ''}. It may be with a dealer or archived. Ask your manager if you need it moved.
        </p>
      )}

      {row && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Enquiry date" value={row.enquiryDate} />
            <Field label="Requirement" value={row.requirement} />
            <Field label="Capacity" value={row.capacity} />
            <Field label="Timeline" value={row.timeline} />
            <Field label="State" value={row.state} />
            <Field label="Checked" value={row.verifiedStatus === 'verified' ? `Verified${row.verifiedByName ? ` by ${row.verifiedByName}` : ''}` : 'Not verified yet'} />
            <Field label="Tier · score" value={[row.tier, row.score != null ? String(row.score) : null].filter(Boolean).join(' · ') || null} />
            <Field label="Owner" value={row.assignedToName ?? row.teamName ?? (row.assignedDealerName ? `Dealer: ${row.assignedDealerName}` : 'Unassigned')} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => ctx.openPicker(row, owned ? 'reassign' : 'assign')} className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700">
              {owned ? 'Reassign ▾' : 'Assign to ▾'}
            </button>
            <button onClick={() => ctx.openVerify(row)} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">
              {row.verifiedStatus === 'verified' ? 'Check details' : 'Verify'}
            </button>
            <button onClick={() => ctx.setResponsesOpenFor(row)} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50">
              Bot chat (Responses)
            </button>
          </div>
        </>
      )}
    </section>
  );
}
